import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import {
  streamingAccountService,
  StreamingAccount,
  ESlotStatus,
} from "@/services/streaming-account";
import { Check, ChevronsUpDown, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";

interface StreamingAccountComboboxProps {
  value: StreamingAccount | null;
  onChange: (account: StreamingAccount | null) => void;
  serviceId: number;
  disabled?: boolean;
}

export function StreamingAccountCombobox({
  value,
  onChange,
  serviceId,
  disabled,
}: StreamingAccountComboboxProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 400);
    return () => clearTimeout(timer);
  }, [search]);

  const { data: accountsResponse, isLoading } = useQuery({
    queryKey: ["streaming-accounts", "search", serviceId, debouncedSearch],
    queryFn: () =>
      streamingAccountService.getAll({
        serviceId,
        email: debouncedSearch || undefined,
        limit: 20,
      }),
    enabled: open && !!serviceId,
  });

  const accounts = accountsResponse?.data || [];

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="h-9 w-full justify-between font-normal"
          disabled={disabled}
        >
          {value ? (
            <span className="flex items-center gap-2 truncate">
              <span>{value.email}</span>
              <span className="text-muted-foreground text-xs">
                ({value.slots.filter((s) => s.status === ESlotStatus.AVAILABLE).length} slots libres)
              </span>
            </span>
          ) : (
            <span className="text-muted-foreground">Buscar cuenta...</span>
          )}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Buscar por email..."
            value={search}
            onValueChange={setSearch}
          />
          <CommandList>
            {isLoading || search !== debouncedSearch ? (
              <div className="flex items-center justify-center py-6">
                <Loader2 className="h-4 w-4 animate-spin" />
              </div>
            ) : accounts.length === 0 ? (
              <CommandEmpty>
                {debouncedSearch ? "No se encontraron cuentas" : "Escribe para buscar"}
              </CommandEmpty>
            ) : (
              <CommandGroup>
                {accounts.map((account) => {
                  const availableCount = account.slots.filter(
                    (s) => s.status === ESlotStatus.AVAILABLE
                  ).length;
                  return (
                    <CommandItem
                      key={account.id}
                      value={String(account.id)}
                      disabled={availableCount === 0}
                      onSelect={() => {
                        if (availableCount === 0) return;
                        onChange(account);
                        setOpen(false);
                      }}
                    >
                      <Check
                        className={cn(
                          "mr-2 h-4 w-4",
                          value?.id === account.id ? "opacity-100" : "opacity-0"
                        )}
                      />
                      <div className="flex flex-col">
                        <span>{account.email}</span>
                        <span className="text-xs text-muted-foreground">
                          {availableCount} slots libres
                          {availableCount === 0 && " · Sin disponibilidad"}
                        </span>
                      </div>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
